import { Button as AntdButton } from "antd";
import moment from "moment";
import { FC, useEffect, useMemo, useState } from "react";
import FontFa from "../../../../components/fontFa/fontFa";
import { InputSelect, InputText } from "../../../../components/input";
import { IInputArrayDataStructureItem, IInputArrayFormProps } from "../../../../components/input/arrayData";
import { InputDateRangePicker } from "../../../../components/input/date-range-picker";
import ModalNotification, { NotificationType } from "../../../../components/modal-notification";
import { MODULE } from "../../../../constants/vosConstants";
import { convertToDate, getDDMMYYYFormat } from "../../../../helper/dateTimeHelper";
import { Button } from "../../../../modules/button";
import { InputWraper, useForm } from "../../../../modules/form";
import BusinessFormComponent, { checkDuplicateDates } from "../../../../modules/form/businessForm";
import { ObjectUtils } from "../../../../modules/utils";
import type { HrDataSearchItem } from "../../../../services/vos/permission-lookup/hr-data-search.service";
import SearchEmployeeModal from "./components/search-employee-modal";
import SelectSeatClass from "./components/select-seat-class";
import { useBusinessTicketContext } from "./contexts/business-ticket-context";

export const BusinessTicketContentStructure: IInputArrayDataStructureItem[] = [
  {
    name: "Name",
    label: "Họ và tên (Tiếng Việt có dấu)",
    isVisibleOnList: true,
  },
  {
    name: "Position",
    label: "Chức vụ",
    isVisibleOnList: true,
  },
  {
    name: "Class",
    label: "Hạng ghế",
    isVisibleOnList: true,
  },
  {
    name: "TickNumber",
    label: "Số vé (Trường hợp hoàn vé)",
    isVisibleOnList: true,
  },
  {
    name: "TripBusiness",
    label: `Hành trình (VD: Nơi đi: SGN, Nơi đến: DAD, Ngày: 19/09/2023 02:45CH)`,
    isVisibleOnList: true,
    defaultValue: [],
  },
  // {
  //   name: "FlightAway",
  //   label: "Chuyến bay đi / giờ / ngày",
  //   isVisibleOnList: true,
  // },
  // {
  //   name: "ReturnFlight",
  //   label: "Chuyến bay về / giờ / ngày",
  //   isVisibleOnList: true,
  // },
];

const hasInvalidTripDateOrder = (trips: any[]): boolean => {
  if (!trips || trips.length < 2) return false;
  for (let i = 1; i < trips.length; i += 1) {
    const prev = moment(trips[i - 1].date);
    const current = moment(trips[i].date);
    if (current.isSameOrBefore(prev, "day")) {
      return true;
    }
  }
  return false;
};

export const BusinessTicketContentFormV2: FC<IInputArrayFormProps> = (props) => {
  const { childFormData } = props;

  const { department } = useBusinessTicketContext();

  const [typeClassSelected, setTypeClassSelected] = useState(null as any);
  const [employeesSelected, setEmployeesSelected] = useState<any>([]);
  const [employeeKeyword, setEmployeeKeyword] = useState("");
  const [searchEmployeeModalOpen, setSearchEmployeeModalOpen] = useState(false);
  const [notificationModal, setNotificationModal] = useState<{
    open: boolean;
    type: NotificationType;
    title: string;
    message: string;
  }>({
    open: false,
    type: "warning",
    title: "",
    message: "",
  });

  const fallbackEmployeeOption = useMemo(() => {
    if (!childFormData) return null;
    const fullName = childFormData.Name || childFormData.FullName;
    if (!fullName) return null;
    return {
      id: childFormData.id || fullName,
      label: fullName,
      value: {
        FullName: fullName,
        Name_Job_Title: childFormData.Position || childFormData.JobTitle_NameVN || "",
        Level_2_Name:
          childFormData.Department ||
          childFormData.Dep_Name_VN ||
          childFormData.Dep_Name ||
          childFormData.Unit ||
          childFormData.department ||
          "",
      },
    };
  }, [childFormData]);

  const isEditing = props.isUpdateUI && !!childFormData;

  useEffect(() => {
    if (!isEditing) return;
    if (fallbackEmployeeOption) setEmployeesSelected([fallbackEmployeeOption]);
    if (childFormData?.Class) setTypeClassSelected(childFormData.Class);
  }, [isEditing, childFormData, fallbackEmployeeOption]);

  const { handleSubmit, getInputProps } = useForm({
    structure: props.structure.map((item) => {
      return {
        ...item,
        defaultValue: ObjectUtils.getIn(childFormData, item.name) || item.defaultValue,
      };
    }),
    onSubmit: (values) => {
      values.Class = typeClassSelected ? typeClassSelected : values.Class;
      const currentEmployees = employeesSelected.length
        ? employeesSelected
        : fallbackEmployeeOption
        ? [fallbackEmployeeOption]
        : [];
      if (values.TripBusiness.length > 0) {
        const trip = [...values.TripBusiness];
        let count = 0;
        trip.forEach((e: any) => {
          if (!e.from || !e.to) count++;
        });
        if (count > 0) {
          setNotificationModal({
            open: true,
            type: "warning",
            title: "Thông báo",
            message: "Vui lòng nhập đầy đủ thông tin hành trình!",
          });
          return;
        }
      }
      if (!currentEmployees.length || (!values.Class && !props.isOnlyPassport)) {
        setNotificationModal({
          open: true,
          type: "warning",
          title: "Thông báo",
          message: "Vui lòng nhập đầy đủ thông tin yêu cầu!",
        });
        return;
      }
      if (values.TripBusiness.length < 2) {
        setNotificationModal({
          open: true,
          type: "warning",
          title: "Thông báo",
          message: "Vui lòng nhập ít nhất 2 chặng (cả chặng đi và chặng về) !",
        });
        return;
      }
      if (checkDuplicateDates(values.TripBusiness)) {
        setNotificationModal({
          open: true,
          type: "warning",
          title: "Thông báo",
          message: "Có ngày trùng lặp trong hành trình. Vui lòng kiểm tra lại!",
        });
        return;
      }
      if (hasInvalidTripDateOrder(values.TripBusiness)) {
        setNotificationModal({
          open: true,
          type: "warning",
          title: "Thông báo",
          message: "Ngày hành trình sau phải lớn hơn ngày hành trình trước. Vui lòng kiểm tra lại!",
        });
        return;
      }
      const payloads = currentEmployees.map((item: any) => ({
        ...values,
        Name: item.value.UserName,
        Position: item.value.Name_Job_Title,
        Department: item.value.Level_2_Name,
      }));

      if (isEditing) {
        props.onSubmit(payloads[0]);
        return;
      }

      props.onSubmit(payloads);
    },
  });

  const onTypeVehicleChange = (e: any) => {
    setTypeClassSelected(e);
  };

  // Convert từ HrDataSearchItem[] sang format hiện tại {id, label, value}[]
  const convertHrDataToEmployeeOption = (hrData: HrDataSearchItem) => {
    return {
      id: hrData.HR_ID,
      label: hrData.UserName,
      value: {
        UserName: hrData.UserName,
        Name_Job_Title: hrData.Name_Job_Title || "",
        Level_2_Name: hrData.Level_2_Name || "",
        FullName: hrData.UserName,
        UserCode: hrData.UserCode,
      },
    };
  };

  // Convert từ format hiện tại sang HrDataSearchItem[]
  const convertEmployeeOptionToHrData = (employees: any[]): HrDataSearchItem[] => {
    return employees
      .filter((emp) => emp && emp.id)
      .map((emp) => ({
        HR_ID: emp.id,
        UserID: String(emp.id),
        UserCode: emp.value?.UserCode || "",
        UserName: emp.label || emp.value?.UserName || "",
        UserType: "",
        Level_1_Code: "",
        Level_2_Code: "",
        Level_3_Code: "",
        Level_1_Name: "",
        Level_2_Name: emp.value?.Level_2_Name || "",
        Level_3_Name: "",
        Code_Job_Title: "",
        Name_Job_Title: emp.value?.Name_Job_Title || "",
        Dep_Code: "",
        Dep_Name: "",
        Email: "",
      }));
  };

  const handleConfirmEmployeeSelection = (
    selectedEmployeesData: HrDataSearchItem[],
    changeHandler?: (value: any) => void,
  ) => {
    // Convert từ HrDataSearchItem[] sang format hiện tại
    const newEmployees = selectedEmployeesData.map(convertHrDataToEmployeeOption);

    // Merge với danh sách hiện tại, loại bỏ trùng lặp
    setEmployeesSelected((prev: any[]) => {
      const merged = [...prev, ...newEmployees].reduce((acc: any[], curr: any) => {
        if (acc.some((e) => e.id === curr.id)) return acc;
        return [...acc, curr];
      }, []);

      if (changeHandler) {
        changeHandler(merged);
      }

      return merged;
    });

    setSearchEmployeeModalOpen(false);
  };

  const handleRemoveEmployee = (employeeId: number, changeHandler?: (value: any) => void) => {
    const nextValue = employeesSelected.filter((item: any) => item.id !== employeeId);
    setEmployeesSelected(nextValue);
    if (changeHandler) {
      changeHandler(nextValue);
    }
    setEmployeeKeyword("");
  };

  const filteredSelectedEmployees = useMemo(() => {
    const keyword = employeeKeyword.trim().toLowerCase();
    if (!keyword) return employeesSelected;
    return employeesSelected.filter((employee: any) => {
      const name = employee?.label?.toLowerCase() || "";
      const position = employee?.value?.JobTitle_NameVN?.toLowerCase() || "";
      const department = employee?.value?.Dep_Name_VN?.toLowerCase() || "";
      return name.includes(keyword) || position.includes(keyword) || department.includes(keyword);
    });
  }, [employeeKeyword, employeesSelected]);

  return (
    <div className="Form__add-demandForSupplies py-3">
      <h6>
        ( <span className="star_content">*</span> ): Trường thông tin bắt buộc phải nhập{" "}
      </h6>

      <InputWraper
        className="pb-3"
        inputProps={getInputProps("Name")}
        isRequired
        renderInput={(s) => (
          <div className="business-ticket__employee-picker flex flex-col p-2">
            <div className="flex justify-end">
              <AntdButton type="primary" onClick={() => setSearchEmployeeModalOpen(true)} disabled={!department}>
                + Thêm nhân viên
              </AntdButton>
            </div>
            <div className="business-ticket__employee-table border mt-2">
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>STT</th>
                      <th>Họ tên</th>
                      <th>Chức vụ</th>
                      <th>Phòng ban</th>
                      <th style={{ width: 100 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSelectedEmployees.length ? (
                      filteredSelectedEmployees.map((employee: any, index: number) => (
                        <tr key={employee.id}>
                          <td>{index + 1}</td>
                          <td>{employee.label}</td>
                          <td>{employee.value?.Name_Job_Title || "--"}</td>
                          <td>{employee.value?.Level_2_Name || "--"}</td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-link d-inline-flex align-items-center gap-1 p-0 delete-button text-danger"
                              onClick={() => handleRemoveEmployee(employee.id, s.onChange)}
                              aria-label="Delete nhân viên"
                            >
                              <FontFa name="faTrash" className="text-danger" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          Chưa có nhân viên nào được chọn
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      />

      {/* <InputWraper inputProps={getInputProps("Name")} renderInput={(s) => <InputText {...s} />} isRequired /> */}
      <InputWraper
        inputClassName="border-none"
        inputProps={getInputProps("Class")}
        renderInput={(s) => (
          <SelectSeatClass
            {...s}
            onChange={onTypeVehicleChange}
            value={typeClassSelected ? typeClassSelected : s.value}
          />
        )}
        isRequired={!props.isOnlyPassport}
      />
      <InputWraper
        inputProps={getInputProps("TripBusiness")}
        renderInput={(s) => <BusinessFormComponent {...s} />}
        isRequired
      />
      <div className="foot">
        <Button type="button" label="Xác nhận" onClick={handleSubmit} />
      </div>

      <SearchEmployeeModal
        module={MODULE.TICKET}
        unit={
          department ? (typeof department === "string" ? Number(department.split("|")[0]) : Number(department)) : null
        }
        open={searchEmployeeModalOpen}
        setOpen={setSearchEmployeeModalOpen}
        onConfirm={(selectedEmployees: HrDataSearchItem[]) =>
          handleConfirmEmployeeSelection(selectedEmployees, getInputProps("Name").onChange)
        }
        existingEmployees={convertEmployeeOptionToHrData(employeesSelected)}
      />

      <ModalNotification
        open={notificationModal.open}
        type={notificationModal.type}
        title={notificationModal.title}
        message={notificationModal.message}
        onCancel={() => setNotificationModal({ ...notificationModal, open: false })}
        showCancel={false}
      />
    </div>
  );
};

export const PassportContentStructure: IInputArrayDataStructureItem[] = [
  {
    name: "PassportForPerson",
    label: "Cấp giấy cho ông bà",
    isVisibleOnList: true,
  },
  {
    name: "Position",
    label: "Chức vụ",
    isVisibleOnList: true,
  },
  {
    name: "Unit",
    label: "Đơn vị",
    isVisibleOnList: true,
  },
  {
    name: "SendOnForBusiness",
    label: "Được cử đi công tác",
    isVisibleOnList: true,
  },
  {
    name: "BusinessTime",
    label: "Thời gian đi công tác",
    isVisibleOnList: true,
  },
  {
    name: "TripBusiness",
    label: "Nơi đi - Nơi đến",
    isVisibleOnList: true,
    defaultValue: [],
  },
  {
    name: "Class",
    label: "Hạng vé",
    isVisibleOnList: true,
  },
  // {
  //   name: "BusinessUnit",
  //   label: "Đơn vị công tác",
  //   isVisibleOnList: true,
  // },
  {
    name: "BusinessContent",
    label: "Nội dung công tác",
    isVisibleOnList: true,
  },
  {
    name: "ConfirmBusinessUnit",
    label: "Xác nhận của đơn vị công tác (Ký tên, đóng dấu)",
    isVisibleOnList: true,
  },
  {
    name: "ConfirmPersonBusiness",
    label: "Xác nhận của CB-NV sử dụng xe",
    isVisibleOnList: true,
  },
];

export const PassportContentForm: FC<IInputArrayFormProps> = (props) => {
  const { childFormData } = props;
  const today = new Date();
  const [originDate, destinationDate] = childFormData.BusinessTime.replace(" ", "").split("-");
  const [estimatedTimeOfUse, setEstimatedTimeOfUse] = useState([
    originDate ? new Date(convertToDate(originDate.trim())) : new Date(),
    destinationDate ? convertToDate(destinationDate.trim()) : new Date(),
  ]);
  const TypeClass = ["J", "M-PR4", "M", "ECONOMY"];
  const [typeClassSelected, setTypeClassSelected] = useState(null as any);
  const [notificationModal, setNotificationModal] = useState<{
    open: boolean;
    type: NotificationType;
    title: string;
    message: string;
  }>({
    open: false,
    type: "warning",
    title: "",
    message: "",
  });

  const { handleSubmit, getInputProps } = useForm({
    structure: props.structure.map((item) => {
      return {
        ...item,
        defaultValue: ObjectUtils.getIn(childFormData, item.name) || item.defaultValue,
      };
    }),
    onSubmit: (values) => {
      values.BusinessTime = estimatedTimeOfUse
        ? getDDMMYYYFormat(estimatedTimeOfUse[0]) + "-" + getDDMMYYYFormat(estimatedTimeOfUse[1])
        : values.BusinessTime;
      values.Class = typeClassSelected ? typeClassSelected : values.Class;
      if (
        !values.PassportForPerson ||
        !values.Position ||
        !values.Unit ||
        !values.SendOnForBusiness ||
        !values.BusinessTime ||
        !values.TripBusiness ||
        !values.Class ||
        //  !values.BusinessUnit ||
        !values.BusinessContent
        //  !values.ConfirmPersonBusiness ||
        //  !values.ConfirmBusinessUnit
      ) {
        setNotificationModal({
          open: true,
          type: "warning",
          title: "Thông báo",
          message: "Vui lòng nhập đầy đủ thông tin yêu cầu!",
        });
        return;
      }
      if (values.TripBusiness && values.TripBusiness.length > 0 && checkDuplicateDates(values.TripBusiness)) {
        setNotificationModal({
          open: true,
          type: "warning",
          title: "Thông báo",
          message: "Có ngày trùng lặp trong hành trình. Vui lòng kiểm tra lại!",
        });
        return;
      }
      if (values.TripBusiness && values.TripBusiness.length > 1 && hasInvalidTripDateOrder(values.TripBusiness)) {
        setNotificationModal({
          open: true,
          type: "warning",
          title: "Thông báo",
          message: "Ngày hành trình sau phải lớn hơn ngày hành trình trước. Vui lòng kiểm tra lại!",
        });
        return;
      }
      props.onSubmit(values);
    },
  });

  const onTypeVehicleChange = (e: any) => {
    setTypeClassSelected(e.value);
  };

  return (
    <div className="Form__add-new py-3">
      <h6>
        ( <span className="star_content">*</span> ): Trường thông tin bắt buộc phải nhập{" "}
      </h6>

      <InputWraper
        inputProps={getInputProps("PassportForPerson")}
        renderInput={(s) => <InputText {...s} />}
        isRequired
      />
      <InputWraper inputProps={getInputProps("Position")} renderInput={(s) => <InputText {...s} />} isRequired />
      <InputWraper inputProps={getInputProps("Unit")} renderInput={(s) => <InputText {...s} />} isRequired />
      <InputWraper inputProps={getInputProps("SendOnForBusiness")} renderInput={(s) => <InputText {...s} />} />
      <InputWraper
        inputProps={getInputProps("BusinessTime")}
        renderInput={(s) => (
          <InputDateRangePicker {...s} value={estimatedTimeOfUse} onChange={setEstimatedTimeOfUse} minDate={today} />
        )}
        isRequired
      />
      <InputWraper
        inputProps={getInputProps("TripBusiness")}
        renderInput={(s) => <BusinessFormComponent {...s} isPassport={true} />}
      />
      <InputWraper
        inputProps={getInputProps("Class")}
        renderInput={(s) => (
          <InputSelect
            {...s}
            options={TypeClass.map((e: any) => ({
              label: e,
              value: e,
            }))}
            onChange={onTypeVehicleChange}
            value={typeClassSelected ? typeClassSelected : s.value}
            placeholder="* Vui lòng chọn hạng ghế"
          />
        )}
        isRequired
      />
      {/* <InputWraper
        inputProps={getInputProps("BusinessUnit")}
        renderInput={(s) => <InputText {...s} />}
      /> */}
      <InputWraper inputProps={getInputProps("BusinessContent")} renderInput={(s) => <InputText {...s} />} />
      <InputWraper
        inputProps={getInputProps("ConfirmBusinessUnit")}
        renderInput={(s) => <InputText {...s} isDisabled={true} />}
      />
      <InputWraper
        inputProps={getInputProps("ConfirmPersonBusiness")}
        renderInput={(s) => <InputText {...s} isDisabled={true} />}
      />
      <div className="foot">
        <Button type="button" label="Xác nhận" onClick={handleSubmit} />
      </div>

      <ModalNotification
        open={notificationModal.open}
        type={notificationModal.type}
        title={notificationModal.title}
        message={notificationModal.message}
        onCancel={() => setNotificationModal({ ...notificationModal, open: false })}
        onOk={() => setNotificationModal({ ...notificationModal, open: false })}
      />
    </div>
  );
};
